import {
	getCountries,
	getCountryCallingCode,
	getExampleNumber,
	getExtPrefix,
	type CountryCode
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import metadata from 'libphonenumber-js/metadata.min.json';

import { getCountryCode } from '@/utils/addresses/getCountryCode';

export type PhoneCountryOption = {
	iso: string;
	name: string;
	callingCode: string;
	flagSrc: string;
};

const NO_FLAG_ISOS = new Set(['AC', 'TA']);

export const getFlagIconSrc = (iso2: string): string =>
	`${import.meta.env.BASE_URL}assets/images/flags/4x3/${iso2.toLowerCase()}.svg`;

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

const buildCountryOptions = (): PhoneCountryOption[] => {
	const options: PhoneCountryOption[] = [];

	for (const iso of getCountries()) {
		if (NO_FLAG_ISOS.has(iso)) continue;

		let name: string;
		let callingCode: string;

		try {
			name = regionNames.of(iso) ?? iso;
			callingCode = getCountryCallingCode(iso);
		} catch {
			continue;
		}

		options.push({ iso, name, callingCode, flagSrc: getFlagIconSrc(iso) });
	}

	return options.sort((a, b) => a.name.localeCompare(b.name));
};

export const PHONE_COUNTRY_OPTIONS: PhoneCountryOption[] =
	buildCountryOptions();

const OPTIONS_BY_ISO = new Map(
	PHONE_COUNTRY_OPTIONS.map(option => [option.iso, option])
);

export const findCountryOptionByIso = (
	iso: string | null | undefined
): PhoneCountryOption | undefined =>
	iso ? OPTIONS_BY_ISO.get(iso) : undefined;

export const getCallingCodeForIso = (iso: string | null | undefined): string =>
	findCountryOptionByIso(iso)?.callingCode ?? '';

const DEFAULT_EXTENSION_PREFIX = 'ext. ';

export const getExtensionPrefixForIso = (
	iso: string | null | undefined
): string => {
	if (!iso) return DEFAULT_EXTENSION_PREFIX;

	try {
		return getExtPrefix(iso as CountryCode).replace(/^\s+/, '');
	} catch {
		return DEFAULT_EXTENSION_PREFIX;
	}
};

const DEFAULT_EXAMPLE_NATIONAL_NUMBER = '202 555 0199';

export const getExampleNationalNumberForIso = (
	iso: string | null | undefined
): string => {
	if (!iso) return DEFAULT_EXAMPLE_NATIONAL_NUMBER;

	try {
		const example = getExampleNumber(iso as CountryCode, examples);
		if (!example) return DEFAULT_EXAMPLE_NATIONAL_NUMBER;

		return example.formatInternational().replace(/^\+\d+\s*/, '');
	} catch {
		return DEFAULT_EXAMPLE_NATIONAL_NUMBER;
	}
};

export const getDefaultCountryIso = (
	countryName: string | null | undefined
): string => {
	const iso = getCountryCode(countryName);
	return findCountryOptionByIso(iso)?.iso ?? '';
};

const buildCallingCodeGroups = (): Map<string, PhoneCountryOption[]> => {
	const groups = new Map<string, PhoneCountryOption[]>();
	for (const option of PHONE_COUNTRY_OPTIONS) {
		const group = groups.get(option.callingCode);
		if (group) group.push(option);
		else groups.set(option.callingCode, [option]);
	}
	return groups;
};

const CALLING_CODE_GROUPS = buildCallingCodeGroups();

const COUNTRY_CALLING_CODES = metadata.country_calling_codes as Record<
	string,
	string[]
>;

export const getMainCountryForCallingCode = (
	callingCode: string
): PhoneCountryOption | undefined =>
	findCountryOptionByIso(COUNTRY_CALLING_CODES[callingCode]?.[0]);

export const resolveInstantCountryForCallingCode = (
	digits: string
): PhoneCountryOption | undefined => {
	const group = CALLING_CODE_GROUPS.get(digits);
	if (!group) return undefined;

	return group.length === 1 ? group[0] : getMainCountryForCallingCode(digits);
};

const CALLING_CODE_LENGTHS = [1, 2, 3];

export const splitCallingCodePrefix = (
	digits: string
): { callingCode: string; rest: string } | undefined => {
	for (const len of CALLING_CODE_LENGTHS) {
		const candidate = digits.slice(0, len);
		if (CALLING_CODE_GROUPS.has(candidate)) {
			return { callingCode: candidate, rest: digits.slice(len) };
		}
	}
	return undefined;
};

export const getCountriesForCallingCode = (
	callingCode: string
): PhoneCountryOption[] => CALLING_CODE_GROUPS.get(callingCode) ?? [];
